import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import CourseView from "./pages/CourseView";
import LessonView from "./pages/LessonView";
import ContactPage from "./pages/ContactPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import PaymentCheckout from "./pages/PaymentCheckout";
import PaymentSuccess from "./pages/PaymentSuccess";
import AvatarDemo from "./pages/AvatarDemo";
import Week1Demo from "./pages/Week1Demo";
import InteractiveLessonDemo from "./pages/InteractiveLessonDemo";
import SimpleInteractiveLesson from "./pages/SimpleInteractiveLesson";
import Week2Lesson from "./pages/Week2Lesson";
import Week3Lesson from "./pages/Week3Lesson";
import Week4Lesson from "./pages/Week4Lesson";
import Week5Lesson from "./pages/Week5Lesson";
import Week6Lesson from "./pages/Week6Lesson";
import Week7Lesson from "./pages/Week7Lesson";
import Week8Lesson from "./pages/Week8Lesson";
import Week9Lesson from "./pages/Week9Lesson";
import Week10Lesson from "./pages/Week10Lesson";
import CodingLesson1 from "./pages/CodingLesson1";
import CodingLesson2 from "./pages/CodingLesson2";
import CodingLesson3 from "./pages/CodingLesson3";
import CodingLesson4 from "./pages/CodingLesson4";
import CodingLesson5 from "./pages/CodingLesson5";
import CodingLesson6 from "./pages/CodingLesson6";
import CodingLesson7 from "./pages/CodingLesson7";
import CodingLesson8 from "./pages/CodingLesson8";
import CodingLesson9 from "./pages/CodingLesson9";
import CodingLesson10 from "./pages/CodingLesson10";
import ProgressDashboard from "./pages/ProgressDashboard";
import DemoChat from "./pages/DemoChat";
import AdminDashboard from "./pages/AdminDashboard";
import AdminLogin from "./pages/AdminLogin";
import AdminFixAccount from "./pages/AdminFixAccount";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout, checkAuth }}>
      <div className="App">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/" />} />
            <Route path="/course/:courseId" element={user ? <CourseView /> : <Navigate to="/" />} />
            <Route path="/lesson/:lessonId" element={user ? <LessonView /> : <Navigate to="/" />} />
            <Route path="/checkout" element={user ? <PaymentCheckout /> : <Navigate to="/" />} />
            <Route path="/payment-success" element={user ? <PaymentSuccess /> : <Navigate to="/" />} />
            <Route path="/avatar-demo" element={<AvatarDemo />} />
            <Route path="/week1-demo" element={<Week1Demo />} />
            <Route path="/coding-demo" element={<CodingLesson1 />} />
            <Route path="/interactive-lesson" element={<InteractiveLessonDemo />} />
            <Route path="/talk-with-geni" element={<SimpleInteractiveLesson />} />
            <Route path="/talk-with-geni-week2" element={<Week2Lesson />} />
            <Route path="/talk-with-geni-week3" element={<Week3Lesson />} />
            <Route path="/talk-with-geni-week4" element={<Week4Lesson />} />
            <Route path="/talk-with-geni-week5" element={<Week5Lesson />} />
            <Route path="/talk-with-geni-week6" element={<Week6Lesson />} />
            <Route path="/talk-with-geni-week7" element={<Week7Lesson />} />
            <Route path="/talk-with-geni-week8" element={<Week8Lesson />} />
            <Route path="/talk-with-geni-week9" element={<Week9Lesson />} />
            <Route path="/talk-with-geni-week10" element={<Week10Lesson />} />
            <Route path="/coding-lesson-1" element={<CodingLesson1 />} />
            <Route path="/coding-lesson-2" element={<CodingLesson2 />} />
            <Route path="/coding-lesson-3" element={<CodingLesson3 />} />
            <Route path="/coding-lesson-4" element={<CodingLesson4 />} />
            <Route path="/coding-lesson-5" element={<CodingLesson5 />} />
            <Route path="/coding-lesson-6" element={<CodingLesson6 />} />
            <Route path="/coding-lesson-7" element={<CodingLesson7 />} />
            <Route path="/coding-lesson-8" element={<CodingLesson8 />} />
            <Route path="/coding-lesson-9" element={<CodingLesson9 />} />
            <Route path="/coding-lesson-10" element={<CodingLesson10 />} />
            <Route path="/my-progress" element={user ? <ProgressDashboard /> : <Navigate to="/" />} />
            <Route path="/demo-chat" element={<DemoChat />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/contact-submissions" element={<AdminDashboard />} />
            <Route path="/admin/fix-account" element={<AdminFixAccount />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthContext.Provider>
  );
}

export default App;
